import { invariantResponse } from '@epic-web/invariant'
import { Link, useSearchParams } from 'react-router'
import { WantToGoButton } from '#app/components/want-to-go-button.tsx'
import { getRegionBySlug, type RegionSlug } from '#app/data/regions.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import {
	getScoreContext,
	getScoreExplainer,
	getScoreSupportText,
} from '#app/utils/consensus.shared.ts'
import { getCuisineCounts } from '#app/utils/cuisine-utils.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { type Route } from './+types/london.$slug.tsx'

// Sort options
const SORT_OPTIONS = [
	{ key: 'rating', label: 'Top Rated' },
	{ key: 'price-low', label: 'Price: Low to High' },
	{ key: 'price-high', label: 'Price: High to Low' },
] as const

type SortKey = (typeof SORT_OPTIONS)[number]['key']

// Price range to numeric value for sorting
function priceToNumber(price: string | null): number {
	if (!price) return 3
	const priceMap: Record<string, number> = {
		'£': 1,
		'££': 2,
		'£££': 3,
		'££££': 4,
	}
	return priceMap[price] ?? 3
}

export const meta: Route.MetaFunction = ({ data }) => {
	if (!data) {
		return [
			{ title: 'Not found | Good to Order' },
			{ name: 'robots', content: 'noindex' },
		]
	}

	const year = new Date().getFullYear()

	if (data.kind === 'region') {
		const title = `Best Restaurants in ${data.region.name} London (${year}) | What to Order`
		const description = `${data.restaurants.length} handpicked spots across ${data.region.name} London, with what to order first at each place.`
		return [
			{ title },
			{ name: 'description', content: description },
			{ property: 'og:title', content: title },
			{ property: 'og:description', content: description },
			{ property: 'og:type', content: 'website' },
			{ property: 'og:url', content: data.canonical },
			{ name: 'twitter:card', content: 'summary_large_image' },
			{ rel: 'canonical', href: data.canonical },
		]
	}

	const { collection } = data
	const title = `${collection.title} (${year}) | What to Order First`
	const description = `${collection.intro.slice(0, 120)} What to order first at each place.`

	return [
		{ title },
		{ name: 'description', content: description },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: data.canonical },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ rel: 'canonical', href: data.canonical },
	]
}

export async function loader({ params, request }: Route.LoaderArgs) {
	const { slug } = params
	invariantResponse(slug, 'Not found', { status: 404 })

	// Get user's want-to-go list if logged in
	const userId = await getUserId(request)
	let wantToGoIds: string[] = []
	if (userId) {
		try {
			wantToGoIds = (
				await prisma.wantToGo.findMany({
					where: { userId },
					select: { restaurantId: true },
				})
			).map((w) => w.restaurantId)
		} catch {
			// Table may not exist yet in production
		}
	}

	const region = getRegionBySlug(slug.toLowerCase() as RegionSlug | string)

	if (region) {
		const domainUrl = getDomainUrl(request)
		const canonical = `${domainUrl}/london/${region.slug}`

		const areaTags = await prisma.tag.findMany({
			where: {
				citySlug: 'london',
				type: 'area',
				slug: { in: region.areaSlugs },
			},
			include: {
				restaurants: {
					where: { restaurant: { status: 'published' } },
				},
			},
		})

		const areaIds = areaTags.map((t) => t.id)
		const restaurants = await prisma.restaurant.findMany({
			where: {
				status: 'published',
				primaryAreaId: { in: areaIds },
			},
			orderBy: { consensusScore: 'desc' },
			include: {
				picks: {
					where: { type: 'safe_first' },
					take: 1,
					orderBy: { sortOrder: 'asc' },
				},
				locations: { orderBy: { sortOrder: 'asc' }, take: 1 },
				tags: { include: { tag: true }, where: { tag: { type: 'area' } } },
			},
		})

		const regionRestaurants = restaurants.map((r) => {
			const safeFirst = r.picks[0]
			const loc = r.locations[0]
			// Prefer googlePlaceId with proper API format for cross-platform support (desktop + mobile)
			const mapUrl = loc?.googlePlaceId
				? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(r.name + ' London')}&query_place_id=${loc.googlePlaceId}`
				: (loc?.mapUrl ?? r.mapUrl)
			return {
				id: r.id,
				name: r.name,
				slug: r.slug,
				cuisine: r.cuisine,
				priceRange: r.priceRange,
				consensusScore: r.consensusScore,
				confidence: r.confidence,
				visited: r.visited,
				isHiddenGem: r.isHiddenGem,
				isCultClassic: r.isCultClassic,
				safeFirstOrder: safeFirst?.title ?? null,
				safeFirstTip: safeFirst?.tip ?? null,
				mapUrl,
				lat: loc?.latitude ?? null,
				lng: loc?.longitude ?? null,
				primaryAreaId: r.primaryAreaId,
				areaTags: r.tags.map((rt) => rt.tag),
				badge: null as string | null,
				isWantToGo: wantToGoIds.includes(r.id),
			}
		})

		const areas = areaTags
			.map((tag) => ({
				id: tag.id,
				name: tag.name,
				slug: tag.slug,
				restaurantCount: tag.restaurants.length,
			}))
			.filter((a) => a.restaurantCount > 0) // Only show areas with restaurants
			.sort((a, b) => b.restaurantCount - a.restaurantCount)

		// Calculate total restaurants in the region
		const totalRestaurants = areas.reduce(
			(sum, a) => sum + a.restaurantCount,
			0,
		)

		const cuisines = (
			await getCuisineCounts({ areaIds, minCount: 1, limit: 24 })
		).map((cuisine) => ({
			slug: cuisine.slug,
			label: cuisine.cuisine,
			count: cuisine.count,
		}))

		return {
			kind: 'region' as const,
			region,
			areas,
			restaurants: regionRestaurants,
			totalRestaurants,
			cuisines,
			canonical,
			domainUrl,
		}
	}

	// Fallback to collection behaviour
	const collection = await prisma.collection.findFirst({
		where: {
			citySlug: 'london',
			slug: slug.toLowerCase(),
			isPublic: true,
		},
		include: {
			restaurants: {
				orderBy: { sortOrder: 'asc' },
				include: {
					restaurant: {
						include: {
							picks: {
								where: { type: 'safe_first' },
								take: 1,
							},
							locations: {
								orderBy: { sortOrder: 'asc' },
								take: 1,
							},
							tags: {
								include: { tag: true },
								where: { tag: { type: 'area' } },
							},
						},
					},
				},
			},
		},
	})

	invariantResponse(collection, 'Collection not found', { status: 404 })

	const canonical = `${getDomainUrl(request)}/london/${collection.slug}`

	const publishedRestaurants = collection.restaurants
		.filter((cr) => cr.restaurant.status === 'published')
		.sort(
			(a, b) =>
				(b.restaurant.consensusScore ?? 0) - (a.restaurant.consensusScore ?? 0),
		)

	const restaurants = publishedRestaurants.map((cr) => {
		const r = cr.restaurant
		const safeFirstPick = r.picks[0]
		const firstLocation = r.locations[0]
		const areaTags = r.tags.map((rt) => rt.tag)
		// Prefer googlePlaceId with proper API format for cross-platform support (desktop + mobile)
		const mapUrl = firstLocation?.googlePlaceId
			? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(r.name + ' London')}&query_place_id=${firstLocation.googlePlaceId}`
			: (firstLocation?.mapUrl ?? r.mapUrl)

		return {
			id: r.id,
			name: r.name,
			slug: r.slug,
			cuisine: r.cuisine,
			priceRange: r.priceRange,
			visited: r.visited,
			confidence: r.confidence,
			consensusScore: r.consensusScore,
			isHiddenGem: r.isHiddenGem,
			isCultClassic: r.isCultClassic,
			mapUrl,
			lat: firstLocation?.latitude ?? null,
			lng: firstLocation?.longitude ?? null,
			safeFirstOrder: safeFirstPick?.title ?? null,
			safeFirstTip: safeFirstPick?.tip ?? null,
			areaTags,
			badge: cr.badge,
			sortOrder: cr.sortOrder,
			isWantToGo: wantToGoIds.includes(r.id),
		}
	})

	const itemList = restaurants.map((r, index) => ({
		'@type': 'ListItem',
		position: index + 1,
		item: {
			'@type': 'Restaurant',
			name: r.name,
			url: `${getDomainUrl(request)}/london/restaurants/${r.slug}`,
			servesCuisine: r.cuisine,
			priceRange: r.priceRange,
			address: r.areaTags[0]
				? {
						'@type': 'PostalAddress',
						addressLocality: r.areaTags[0].name,
						addressRegion: 'London',
						addressCountry: 'GB',
					}
				: undefined,
			geo:
				r.lat != null && r.lng != null
					? {
							'@type': 'GeoCoordinates',
							latitude: r.lat,
							longitude: r.lng,
						}
					: undefined,
		},
	}))

	return {
		kind: 'collection' as const,
		collection: {
			id: collection.id,
			slug: collection.slug,
			title: collection.title,
			intro: collection.intro,
			rules: collection.rules,
		},
		restaurants,
		itemList,
		canonical,
	}
}

export default function Page({ loaderData }: Route.ComponentProps) {
	if (loaderData.kind === 'region') {
		return <RegionPage loaderData={loaderData} />
	}
	return <CollectionPage loaderData={loaderData} />
}

function RegionPage({
	loaderData,
}: {
	loaderData: Extract<Awaited<ReturnType<typeof loader>>, { kind: 'region' }>
}) {
	const {
		region,
		areas,
		restaurants,
		totalRestaurants,
		cuisines,
		canonical,
		domainUrl,
	} = loaderData

	// Sort state from URL
	const [searchParams, setSearchParams] = useSearchParams()
	const currentSort = (searchParams.get('sort') as SortKey) || 'rating'

	// Sort restaurants
	const sortedRestaurants = [...restaurants].sort((a, b) => {
		switch (currentSort) {
			case 'price-low':
				return priceToNumber(a.priceRange) - priceToNumber(b.priceRange)
			case 'price-high':
				return priceToNumber(b.priceRange) - priceToNumber(a.priceRange)
			case 'rating':
			default:
				return (b.consensusScore ?? 0) - (a.consensusScore ?? 0)
		}
	})

	const handleSortChange = (newSort: SortKey) => {
		if (newSort === 'rating') {
			searchParams.delete('sort')
		} else {
			searchParams.set('sort', newSort)
		}
		setSearchParams(searchParams, { replace: true })
	}

	const ldBreadcrumb = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{ '@type': 'ListItem', position: 1, name: 'Home', item: `${domainUrl}/` },
			{
				'@type': 'ListItem',
				position: 2,
				name: 'London',
				item: `${domainUrl}/london`,
			},
			{ '@type': 'ListItem', position: 3, name: region.name, item: canonical },
		],
	}
	const ldItemList = {
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		name: `Best Restaurants in ${region.name} London`,
		numberOfItems: restaurants.length,
		itemListElement: restaurants.map((restaurant, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			item: {
				'@type': 'Restaurant',
				name: restaurant.name,
				url: `${domainUrl}/london/restaurants/${restaurant.slug}`,
				servesCuisine: restaurant.cuisine,
				priceRange: restaurant.priceRange,
				address: restaurant.areaTags[0]
					? {
							'@type': 'PostalAddress',
							addressLocality: restaurant.areaTags[0].name,
							addressRegion: 'London',
							addressCountry: 'GB',
						}
					: undefined,
				geo:
					restaurant.lat != null && restaurant.lng != null
						? {
								'@type': 'GeoCoordinates',
								latitude: restaurant.lat,
								longitude: restaurant.lng,
							}
						: undefined,
			},
		})),
	}

	return (
		<main className="min-h-screen bg-[#05050a] text-[#f8f8ff]">
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(ldItemList) }}
			/>
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(ldBreadcrumb) }}
			/>

			<section className="border-b border-white/10 bg-gradient-to-b from-[#000000] via-[#05050a] to-[#0a001f]">
				<div className="container mx-auto px-6 py-12 lg:py-16">
					<nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-[#b0b0e0]">
						<Link to="/" className="hover:text-[#422ad5]">
							Home
						</Link>
						<span>/</span>
						<span className="text-[#f8f8ff]">London</span>
						<span>/</span>
						<span className="text-[#f8f8ff]">{region.name}</span>
					</nav>

					<div className="max-w-3xl space-y-4">
						<p className="font-mono text-[11px] tracking-[0.3em] text-gray-500 uppercase">
							{region.vibe}
						</p>
						<h1 className="text-4xl font-black tracking-tight text-[#f8f8ff] sm:text-5xl">
							Best Restaurants in {region.name} London
						</h1>
						<p className="text-lg text-[#b0b0e0]">
							{totalRestaurants} curated restaurants across {areas.length}{' '}
							neighbourhoods. Every listing tells you exactly what to order.
						</p>
						<p className="max-w-2xl text-sm text-[#7070a0]">
							Every place here is handpicked. Signal score shows how strong and
							consistent the review coverage is, not a star rating.
						</p>
					</div>

					{/* Filters - Region-specific */}
					<div className="mt-8 flex flex-wrap gap-3">
						<Link
							to={`/london/best/near-me-${region.slug}`}
							className="btn gap-2 border border-[#ff2e63] bg-[#ff2e63]/10 text-[#ff2e63] hover:bg-[#ff2e63]/20"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="currentColor"
								className="size-4"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
								/>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
								/>
							</svg>
							Near Me
						</Link>
						<Link
							to={`/london/best/top-rated-${region.slug}`}
							className="btn border border-[#00e0ff] bg-[#00e0ff]/10 text-[#00e0ff] hover:bg-[#00e0ff]/20"
						>
							Top Rated
						</Link>
						<Link
							to={`/london/best/cheap-eats-${region.slug}`}
							className="btn border border-[#22c55e] bg-[#22c55e]/10 text-[#22c55e] hover:bg-[#22c55e]/20"
						>
							Cheap Eats
						</Link>
						<Link
							to={`/london/best/date-night-${region.slug}`}
							className="btn border border-[#ff9f1c] bg-[#ff9f1c]/10 text-[#ff9f1c] hover:bg-[#ff9f1c]/20"
						>
							Date Night
						</Link>
						<Link
							to={`/london/best/fine-dining-${region.slug}`}
							className="btn border border-[#422ad5] bg-[#422ad5]/10 text-[#f8f8ff] hover:bg-[#422ad5]/20"
						>
							Fine Dining
						</Link>
					</div>
				</div>
			</section>

			<section className="container mx-auto px-6 py-10">
				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
					{areas.map((area) => {
						const count = area.restaurantCount

						return (
							<Link
								key={area.slug}
								to={`/london/best/restaurants-${area.slug}`}
								className="relative overflow-hidden rounded-xl border border-white/10 bg-[#0d0d15] p-6 transition hover:-translate-y-1 hover:shadow-[0_0_28px_rgba(0,224,255,0.2)]"
							>
								<div className="flex items-start justify-between">
									<div>
										<p className="text-xs tracking-[0.2em] text-gray-400 uppercase">
											{region.name}
										</p>
										<h3 className="mt-2 text-2xl font-black tracking-tight text-[#f8f8ff]">
											{area.name}
										</h3>
									</div>
									<span className="badge border-[#00e0ff] bg-[#00e0ff1a] px-3 py-2 tracking-[0.18em] text-[#00e0ff] uppercase">
										{count > 0 ? `${count} spots` : 'Explore'}
									</span>
								</div>
								<p className="mt-3 text-sm text-[#b0b0e0]">
									{count > 0
										? 'See handpicked places and what to order first.'
										: 'Best restaurants coming soon.'}
								</p>
							</Link>
						)
					})}
				</div>
			</section>

			{/* Browse by Cuisine - Region-specific */}
			{cuisines.length > 0 && (
				<section className="container mx-auto px-6 pb-10">
					<h2 className="mb-6 text-2xl font-bold text-[#f8f8ff]">
						By Cuisine in {region.name} London
					</h2>
					<div className="flex flex-wrap gap-3">
						{cuisines.map((cuisine) => (
							<Link
								key={cuisine.slug}
								to={`/london/best/${cuisine.slug}-${region.slug}`}
								className="badge border border-[#422ad5] bg-[#0d0d15] px-4 py-3 text-sm text-[#f8f8ff] transition hover:bg-[#422ad5]/20"
							>
								{cuisine.label}
								<span className="ml-2 text-[#7070a0]">{cuisine.count}</span>
							</Link>
						))}
					</div>
				</section>
			)}

			<section className="container mx-auto px-6 pb-14">
				<div className="mb-6 flex flex-wrap items-center justify-between gap-4">
					<h2 className="text-2xl font-black tracking-tight text-[#f8f8ff]">
						Top critic picks
					</h2>
					<div className="flex items-center gap-2">
						<label htmlFor="region-sort" className="text-sm text-[#7070a0]">
							Sort by:
						</label>
						<select
							id="region-sort"
							value={currentSort}
							onChange={(e) => handleSortChange(e.target.value as SortKey)}
							className="cursor-pointer rounded-lg border border-[#1a1a2e] bg-[#0d0d15] px-3 py-2 text-sm text-[#f8f8ff] focus:border-[#422ad5] focus:outline-none"
						>
							{SORT_OPTIONS.map((opt) => (
								<option key={opt.key} value={opt.key}>
									{opt.label}
								</option>
							))}
						</select>
					</div>
				</div>
				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
					{sortedRestaurants.map((restaurant, index) => (
						<RestaurantCard
							key={restaurant.id}
							restaurant={restaurant}
							rank={index + 1}
						/>
					))}
				</div>

				{sortedRestaurants.length === 0 && (
					<div className="py-12 text-center text-[#b0b0e0]">
						No verified restaurants in this region yet.
					</div>
				)}
			</section>
		</main>
	)
}

function CollectionPage({
	loaderData,
}: {
	loaderData: Extract<
		Awaited<ReturnType<typeof loader>>,
		{ kind: 'collection' }
	>
}) {
	const { collection, restaurants, itemList } = loaderData

	const ldItemList = {
		'@context': 'https://schema.org',
		'@type': 'ItemList',
		name: collection.title,
		description: collection.intro,
		numberOfItems: restaurants.length,
		itemListElement: itemList,
	}

	return (
		<main className="min-h-screen bg-[#05050a] text-[#f8f8ff]">
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(ldItemList) }}
			/>

			<section className="border-b border-[#1a1a2e] bg-gradient-to-b from-[#000000] via-[#05050a] to-[#0a001f]">
				<div className="container mx-auto px-6 py-12 lg:py-16">
					<nav className="mb-6 flex flex-wrap items-center gap-2 text-sm text-[#b0b0e0]">
						<Link to="/" className="hover:text-[#422ad5]">
							Home
						</Link>
						<span>/</span>
						<span className="text-[#f8f8ff]">London</span>
						<span>/</span>
						<span className="text-[#f8f8ff]">{collection.title}</span>
					</nav>

					<div className="max-w-3xl space-y-4">
						<h1 className="text-4xl font-black tracking-tight text-[#f8f8ff] sm:text-5xl">
							{collection.title}
						</h1>
						<p className="text-lg text-[#b0b0e0]">{collection.intro}</p>

						{collection.rules && (
							<div className="alert border border-[#1a1a2e] bg-[#0d0d15] shadow-[0_0_20px_rgba(66,42,213,0.12)]">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth={1.5}
									stroke="currentColor"
									className="size-5 text-[#00e0ff]"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"
									/>
								</svg>
								<span className="text-sm text-[#f8f8ff]">
									{collection.rules}
								</span>
							</div>
						)}

						<p className="text-sm text-[#b0b0e0]">
							{restaurants.length} restaurant
							{restaurants.length !== 1 ? 's' : ''} in this collection
						</p>
					</div>

					{/* Filters */}
					<div className="mt-8 flex flex-wrap gap-3">
						<Link
							to="/london/best/near-me"
							className="btn gap-2 border border-[#ff2e63] bg-[#ff2e63]/10 text-[#ff2e63] hover:bg-[#ff2e63]/20"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="currentColor"
								className="size-4"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
								/>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
								/>
							</svg>
							Near Me
						</Link>
						<Link
							to="/london/best/top-rated"
							className="btn border border-[#00e0ff] bg-[#00e0ff]/10 text-[#00e0ff] hover:bg-[#00e0ff]/20"
						>
							Top Rated
						</Link>
						<Link
							to="/london/best/cheap-eats"
							className="btn border border-[#22c55e] bg-[#22c55e]/10 text-[#22c55e] hover:bg-[#22c55e]/20"
						>
							Cheap Eats
						</Link>
						<Link
							to="/london/best/date-night"
							className="btn border border-[#ff9f1c] bg-[#ff9f1c]/10 text-[#ff9f1c] hover:bg-[#ff9f1c]/20"
						>
							Date Night
						</Link>
						<Link
							to="/london/best/fine-dining"
							className="btn border border-[#422ad5] bg-[#422ad5]/10 text-[#f8f8ff] hover:bg-[#422ad5]/20"
						>
							Fine Dining
						</Link>
					</div>
				</div>
			</section>

			<section className="container mx-auto px-6 py-10">
				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
					{restaurants.map((restaurant, index) => (
						<RestaurantCard
							key={restaurant.id}
							restaurant={restaurant}
							rank={index + 1}
						/>
					))}
				</div>

				{restaurants.length === 0 && (
					<div className="py-16 text-center">
						<p className="text-base-content/60">
							No restaurants in this collection yet.
						</p>
					</div>
				)}
			</section>
		</main>
	)
}

function RestaurantCard({
	restaurant,
	rank,
}: {
	restaurant: {
		id: string
		name: string
		slug: string
		cuisine: string
		priceRange: string
		visited: boolean
		confidence: string
		mapUrl: string | null
		safeFirstOrder: string | null
		safeFirstTip: string | null
		areaTags: { id: string; slug: string; name: string }[]
		badge: string | null
		consensusScore: number
		isHiddenGem?: boolean
		isCultClassic?: boolean
		isWantToGo?: boolean
	}
	rank: number
}) {
	const scoreContext = getScoreContext(restaurant.consensusScore ?? 0)

	return (
		<article className="card border border-[#1a1a2e] bg-[#0d0d15] shadow-[0_0_20px_rgba(66,42,213,0.12)] transition hover:shadow-[0_0_28px_rgba(0,224,255,0.2)]">
			<div className="card-body space-y-4 p-5">
				{/* Header with name and score */}
				<div className="flex items-start justify-between gap-3">
					<div className="flex items-center gap-3">
						<span className="badge badge-lg border border-[#422ad5] font-bold text-[#422ad5]">
							{rank}
						</span>
						<div>
							<Link
								to={`/london/restaurants/${restaurant.slug}`}
								className="card-title text-[#f8f8ff] hover:text-[#00e0ff]"
							>
								{restaurant.name}
							</Link>
							<p className="mt-1 text-xs tracking-[0.1em] text-[#b0b0e0] uppercase">
								{restaurant.cuisine} · {restaurant.priceRange}
							</p>
						</div>
					</div>
					<span
						className="badge badge-sm gap-1 font-semibold"
						style={{
							borderColor: scoreContext.color,
							color: scoreContext.color,
							backgroundColor: `${scoreContext.color}1a`,
						}}
						title={getScoreExplainer(restaurant.consensusScore ?? 0)}
					>
						{restaurant.consensusScore ?? 0}
						<span className="text-[10px] tracking-[0.2em] text-[#b0b0e0] uppercase">
							Signal
						</span>
					</span>
				</div>

				{/* Badges row */}
				<div className="flex flex-wrap gap-2">
					{restaurant.visited && (
						<span className="badge badge-sm border border-[#00e0ff] tracking-[0.1em] text-[#00e0ff] uppercase">
							✓ Visited
						</span>
					)}
					{restaurant.isHiddenGem && (
						<span className="badge badge-sm border border-[#00e0ff] tracking-[0.1em] text-[#00e0ff] uppercase">
							Hidden Gem
						</span>
					)}
					{restaurant.isCultClassic && (
						<span className="badge badge-sm border border-[#ff9f1c] tracking-[0.1em] text-[#ff9f1c] uppercase">
							Cult Classic
						</span>
					)}
					{restaurant.badge && (
						<span className="badge badge-sm border border-[#422ad5] tracking-[0.1em] text-[#f8f8ff] uppercase">
							{restaurant.badge}
						</span>
					)}
					{(restaurant.consensusScore ?? 0) < 45 && !restaurant.isHiddenGem && (
						<span className="badge badge-sm border border-[#7070a0] tracking-[0.1em] text-[#b0b0e0] uppercase">
							Light Coverage
						</span>
					)}
				</div>

				{/* Safe first order */}
				{restaurant.safeFirstOrder && (
					<div className="rounded-lg border border-[#1a1a2e] bg-[#151525] p-3">
						<p className="mb-1 text-xs tracking-wide text-[#b0b0e0] uppercase">
							Start with
						</p>
						<p className="font-semibold text-[#f8f8ff]">
							{restaurant.safeFirstOrder}
						</p>
						{restaurant.safeFirstTip && (
							<p className="mt-1 text-sm text-[#b0b0e0]">
								{restaurant.safeFirstTip}
							</p>
						)}
					</div>
				)}

				<p className="text-xs text-[#7070a0]">
					{getScoreSupportText(restaurant.consensusScore ?? 0)}
				</p>

				{/* Actions */}
				<div className="card-actions items-center justify-between pt-2">
					{restaurant.mapUrl && (
						<a
							href={restaurant.mapUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="btn btn-ghost btn-sm gap-1 text-[#f8f8ff] hover:text-[#00e0ff]"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="currentColor"
								className="size-4"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
								/>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
								/>
							</svg>
							Map
						</a>
					)}
					<div className="flex items-center gap-2">
						<WantToGoButton
							restaurantId={restaurant.id}
							initialIsWantToGo={restaurant.isWantToGo}
							size="sm"
						/>
						<Link
							to={`/london/restaurants/${restaurant.slug}`}
							className="btn btn-primary btn-sm"
						>
							What to order →
						</Link>
					</div>
				</div>
			</div>
		</article>
	)
}
